import React, { useState, useEffect, useLayoutEffect } from "react";
import { Card } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { useFormContext } from "react-hook-form";

import { StopType } from "types/MultiStop";
import useHeaderData from "./useHeaderData";
import HeaderTitle from "./HeaderTitle";
import HeaderActions from "./HeaderActions";
import FreightModeCard from "./FreightModeCard";
import LocationCard from "./LocationCard";
import MultiStopLocationCard from "./MultiStopLocationCard";
import IncotermCard from "./IncotermCard";
import ProviderCard from "./ProviderCard";
import TotalWeightCard from "./TotalWeightCard";
import LoadTemplateDialog from "../LoadTemplateDialog/LoadTemplateDialog";
import useGetMultiStop from "../Schedule/hooks/useGetMultiStop";
import "./Header.scss";

interface IHeaderProps {
  isQuoteRequest: boolean;
  state: any;
  isEditShipment?: boolean;
  isNewShipment?: boolean;
  isOffPlatformShipment?: boolean;
  isMiniTender?: boolean;
  isQuickRequest?: boolean;
}

function Header({
  isQuoteRequest,
  state,
  isEditShipment,
  isOffPlatformShipment,
  isMiniTender,
  isNewShipment,
  isQuickRequest,
}: IHeaderProps) {
  const [templateDialogOpen, setTemplateDialogOpen] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("template_id");

  const { isMultiStopQuoteRequest } = useGetMultiStop();
  const { getValues, watch } = useFormContext();

  const stops: any[] = watch("schedule.stops") || [];
  const firstPickup = stops.find((s: any) => s.stop_type === StopType.PICKUP);
  const lastDelivery = [...stops].reverse().find((s: any) => s.stop_type === StopType.DROPOFF);
  const multiStopEndPoints = [firstPickup, lastDelivery].filter(Boolean);

  const {
    isPickupReady,
    isDeliveryReady,
    freightMode,
    offPlatformProvider,
    providers,
    providersList,
    incoterm,
    fromCountry,
    toCountry,
    total,
    providersDisplayLimit,
  } = useHeaderData({ isOffPlatformShipment, state });

  useEffect(() => {
    if (templateId) {
      setTemplateDialogOpen(true);
    }
  }, [templateId]);

  useLayoutEffect(() => {
    const formHeader = document.getElementById("formHeader");
    const requestForm = document.getElementById("request-form");
    const fixedHeader = (e: any) => {
      if (e.target.tagName === "TEXTAREA" || e.target.tagName === "NAV" || e.target.classList.contains("dropdown-menu"))
        return;

      const amountScrolled = e.srcElement.scrollTop;

      if (formHeader) {
        if (amountScrolled > 30) {
          formHeader.classList.add("fixedTop");
          requestForm?.classList.add("stickyMode");
        } else {
          formHeader.classList.remove("fixedTop");
          requestForm?.classList.remove("stickyMode");
        }
      }
    };
    window.addEventListener("scroll", fixedHeader, true);
  }, []);

  return (
    <Card className="mb-3 form-container formHeader" id="formHeader">
      {!isOffPlatformShipment && (
        <LoadTemplateDialog
          isOpen={templateDialogOpen}
          close={() => {
            setTemplateDialogOpen(false);
          }}
          isQuoteRequest={isQuoteRequest}
          isNewShipment={isNewShipment}
          formVersion="v1"
        />
      )}
      <div>
        <div className="px-4 d-flex justify-content-between align-items-center">
          <h3 className="pt-16 mb-3">
            <HeaderTitle
              isQuoteRequest={isQuoteRequest}
              isNewShipment={isNewShipment}
              isMiniTender={isMiniTender}
              isEditShipment={isEditShipment}
              isOffPlatformShipment={isOffPlatformShipment}
            />
          </h3>
          <HeaderActions
            isOffPlatformShipment={isOffPlatformShipment}
            isQuoteRequest={isQuoteRequest}
            isQuickRequest={isQuickRequest}
            onOpenTemplate={() => setTemplateDialogOpen(true)}
          />
        </div>
        <div className="d-flex flex-row pl-16 overflow-auto cardItem-gap">
          {(isQuoteRequest || isOffPlatformShipment) && <FreightModeCard freightModes={freightMode} />}

          {isMultiStopQuoteRequest ? (
            multiStopEndPoints.map((stop: any, index: number) => (
              <MultiStopLocationCard
                key={stop.stop_key || index}
                stop={stop}
                index={index}
                totalStops={multiStopEndPoints.length}
              />
            ))
          ) : (
            <>
              <LocationCard
                label={<FormattedMessage id="new-request.schedule.specifyHoursFrom" defaultMessage="From" />}
                country={getValues("schedule.pickup_country")}
                airportCode={getValues("schedule.pickup_airport_code")}
                seaportCode={getValues("schedule.pickup_seaport_code")}
                city={getValues("schedule.pickup_city")}
                resolvedCountryLabel={fromCountry()?.label}
                resolvedCountryValue={fromCountry()?.value}
                isSameCountry={fromCountry()?.value === toCountry()?.value}
                isReady={isPickupReady}
                asapValue="asap_pickup"
                readyTime={getValues("schedule.pickup_ready_time")}
                readyDate={getValues("schedule.pickup_ready_date")}
              />

              <LocationCard
                label={<FormattedMessage id="new-request.schedule.specifyHoursTo" defaultMessage="To" />}
                country={getValues("schedule.stop1_country")}
                airportCode={getValues("schedule.stop1_airport_code")}
                seaportCode={getValues("schedule.stop1_seaport_code")}
                city={getValues("schedule.stop1_city")}
                resolvedCountryLabel={toCountry()?.label}
                resolvedCountryValue={toCountry()?.value}
                isSameCountry={fromCountry()?.value === toCountry()?.value}
                isReady={isDeliveryReady}
                asapValue="asap_delivery"
                readyTime={getValues("schedule.stop1_after_time")}
                readyDate={getValues("schedule.stop1_after_date")}
              />
            </>
          )}

          {!isOffPlatformShipment && <IncotermCard incoterm={incoterm} />}

          {(isQuoteRequest || isOffPlatformShipment) && (
            <ProviderCard
              isOffPlatformShipment={isOffPlatformShipment}
              offPlatformProvider={offPlatformProvider}
              providersList={providersList}
              providers={providers}
              displayLimit={providersDisplayLimit}
            />
          )}

          {!isOffPlatformShipment && <TotalWeightCard total={total} weightUnit={getValues("packing_list.0.w_unit")} />}
        </div>
      </div>
    </Card>
  );
}

export default Header;

Header.defaultProps = {
  isEditShipment: false,
  isNewShipment: false,
  isOffPlatformShipment: false,
  isMiniTender: false,
  isQuickRequest: false,
};
